/**
 * useSanctaIdle — idle → sleep state machine for the Sancta widget.
 *
 * After `timeoutMs` (default 2.5 min) of no user activity, Sancta enters
 * "sleep" (animations stop, widget dims). Any poke() (click / touch / key /
 * voice / send) wakes it instantly. No AI calls are made while asleep —
 * AI only ever fires on an explicit send, so sleep is purely a visual +
 * activity-tracking state that also lets consumers pause polling.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export function useSanctaIdle(timeoutMs: number = 150000, enabled: boolean = true) {
  const [asleep, setAsleep] = useState(false);
  const lastRef = useRef<number>(Date.now());

  const poke = useCallback(() => {
    lastRef.current = Date.now();
    setAsleep((s) => (s ? false : s));
  }, []);

  useEffect(() => {
    if (!enabled) { setAsleep(false); return; }
    const id = setInterval(() => {
      if (Date.now() - lastRef.current >= timeoutMs) setAsleep(true);
    }, 5000);
    return () => clearInterval(id);
  }, [timeoutMs, enabled]);

  return { asleep, poke };
}

export default useSanctaIdle;
